import { Trophy, ChartBar, CaretRight } from "@phosphor-icons/react";

export const EVENT_LABELS = {
  page_view: "Page Views",
  hero_cta_click: "Hero CTA Clicks",
  nav_book_cta_click: "Nav 'Book a Call' Clicks",
  calendly_click_15min: "Calendly 15-min Clicks",
  calendly_click_60min: "Calendly 60-min Clicks",
  checklist_view: "Checklist Section Views",
  checklist_submit: "Checklist Form Submits",
  checklist_download: "Checklist PDF Downloads",
  contact_submit: "Contact Form Submits",
};

// -----------------------------------------------------------------------------
// Small primitives (Stat card + Section header)
// -----------------------------------------------------------------------------
export function Stat({ label, value, hint }) {
  return (
    <div className="bg-cream border border-sage-soft p-6 lg:p-8">
      <div className="text-[10px] tracking-overline uppercase text-sage font-semibold mb-3">
        {label}
      </div>
      <div className="font-serif text-4xl lg:text-5xl text-deep-teal leading-none">
        {value}
      </div>
      {hint && <div className="mt-2 text-xs text-charcoal-teal/70">{hint}</div>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// A/B Test panel
// -----------------------------------------------------------------------------
function leadingVariantOf(a, b) {
  if (a.conversion === b.conversion) return null;
  return a.conversion > b.conversion ? "A" : "B";
}

function buildVariants(ab) {
  return [
    {
      id: "A",
      label: "Variant A — 'Send Me the Checklist'",
      views: ab.views?.A || 0,
      submits: ab.submits?.A || 0,
      conversion: ab.conversion?.A || 0,
    },
    {
      id: "B",
      label: "Variant B — 'Get My Free IRA Tax Audit'",
      views: ab.views?.B || 0,
      submits: ab.submits?.B || 0,
      conversion: ab.conversion?.B || 0,
    },
  ];
}

function VariantCard({ v, isWinning }) {
  return (
    <div
      data-testid={`admin-variant-${v.id}`}
      className={`p-8 transition-colors ${isWinning ? "bg-sage/15" : "bg-deep-teal"}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="font-serif italic text-sage text-2xl">{v.id}.</span>
        {isWinning && (
          <span className="text-[10px] tracking-overline uppercase font-semibold text-sage bg-sage/10 px-2 py-1 border border-sage/30">
            Leading
          </span>
        )}
      </div>
      <div className="font-serif text-xl text-cream mb-6 leading-snug">
        {v.label}
      </div>
      <div className="grid grid-cols-3 gap-4 pt-6 border-t border-sage/20">
        {[
          ["Views", v.views],
          ["Submits", v.submits],
          ["Convert", `${v.conversion}%`],
        ].map(([k, val]) => (
          <div key={k}>
            <div className="text-[10px] tracking-overline uppercase text-sage/80 mb-1">
              {k}
            </div>
            <div className="font-serif text-2xl text-cream">{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ABTestPanel({ ab }) {
  const variants = buildVariants(ab || {});
  const leading = leadingVariantOf(variants[0], variants[1]);
  return (
    <section
      className="bg-deep-teal text-cream p-8 lg:p-12 mb-16"
      data-testid="admin-ab-test-block"
    >
      <div className="flex items-center gap-3 mb-6">
        <Trophy size={20} weight="light" className="text-sage" />
        <span className="text-xs tracking-overline uppercase font-semibold text-sage">
          A/B Test &middot; Checklist CTA
        </span>
      </div>
      <h2 className="font-serif text-3xl lg:text-4xl mb-8 leading-tight">
        Which headline converts better?
      </h2>
      <div className="grid md:grid-cols-2 gap-px bg-sage/30 border border-sage/30">
        {variants.map((v) => (
          <VariantCard key={v.id} v={v} isWinning={leading === v.id} />
        ))}
      </div>
      <p className="mt-6 text-xs italic text-cream/60">
        Variants are randomly assigned per visitor and persisted in their
        browser. Conversion = submits ÷ views.
      </p>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Event breakdown
// -----------------------------------------------------------------------------
export function EventBreakdown({ summary }) {
  const eventCount = (name) =>
    summary?.by_event?.find((e) => e.event === name)?.count || 0;
  return (
    <section className="mb-16">
      <div className="flex items-center gap-3 mb-6">
        <ChartBar size={20} weight="light" className="text-sage" />
        <span className="text-xs tracking-overline uppercase font-semibold text-sage">
          Event Breakdown
        </span>
      </div>
      <div className="border-t border-sage-soft" data-testid="admin-event-breakdown">
        {Object.keys(EVENT_LABELS).map((key) => (
          <div
            key={key}
            className="flex items-center justify-between border-b border-sage-soft py-4"
          >
            <div className="flex items-center gap-3">
              <CaretRight size={14} weight="light" className="text-sage" />
              <span className="text-base text-deep-teal">
                {EVENT_LABELS[key]}
              </span>
            </div>
            <span className="font-serif text-2xl text-deep-teal">
              {eventCount(key)}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Leads + Inquiries tables
// -----------------------------------------------------------------------------
function LeadRow({ l }) {
  return (
    <div className="border-b border-sage-soft py-4 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="font-serif text-lg text-deep-teal truncate">
          {l.name}
        </div>
        <div className="text-xs text-charcoal-teal/70 truncate">{l.email}</div>
      </div>
      <div className="text-right">
        <div className="text-[10px] tracking-overline uppercase text-sage font-semibold">
          Variant {l.variant || "—"}
        </div>
        <div className="text-xs text-charcoal-teal/60 mt-1">
          {new Date(l.created_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}

function InquiryRow({ q }) {
  return (
    <div className="border-b border-sage-soft py-4">
      <div className="flex items-center justify-between gap-4 mb-1">
        <div className="font-serif text-lg text-deep-teal truncate">{q.name}</div>
        <div className="text-xs text-charcoal-teal/60">
          {new Date(q.created_at).toLocaleDateString()}
        </div>
      </div>
      <div className="text-xs text-charcoal-teal/70 mb-1">
        {q.email} {q.phone ? `· ${q.phone}` : ""}
      </div>
      {q.interest && (
        <div className="text-[10px] tracking-overline uppercase text-sage font-semibold">
          {q.interest}
        </div>
      )}
    </div>
  );
}

export function LeadsTable({ title, items, testid, emptyText, RowComponent }) {
  return (
    <div>
      <div className="text-xs tracking-overline uppercase font-semibold text-sage mb-4">
        {title}
      </div>
      <div className="border-t border-sage-soft" data-testid={testid}>
        {items.length === 0 ? (
          <p className="py-6 text-sm text-charcoal-teal/70 italic">
            {emptyText}
          </p>
        ) : (
          items.slice(0, 10).map((item) => (
            <RowComponent key={item.id} l={item} q={item} />
          ))
        )}
      </div>
    </div>
  );
}

export { LeadRow, InquiryRow };
